import { useEffect, useMemo, useState } from "react";
import { openInvoicePdf } from "../utils/openPdf";

import {
  fetchInvoiceInitiatorQueue,
  recalculateInvoiceInitiatorQueue,
  submitInvoiceInitiatorToWorkflow,
} from "../api";

const QUEUE_TABS = [
  {
    key: "unidentified",
    label: "Unidentified Invoices",
    queueType: "UNIDENTIFIED_INVOICE",
  },
  {
    key: "nonPoConditions",
    label: "Non-PO Conditions",
    queueType: "NON_PO_MULTIPLE_CONDITION",
  },
  {
    key: "duplicates",
    label: "Duplicate Invoice",
    queueType: "DUPLICATE_INVOICE",
  },
  {
    key: "poReview",
    label: "PO Review",
    queueType: "PO_MISSING_VENDOR_OR_INVOICE",
  },
];

function formatAmount(value) {
  if (!value) {
    return "-";
  }

  const numberValue = Number(value);

  if (Number.isNaN(numberValue)) {
    return value;
  }

  return numberValue.toLocaleString("en-IN", {
    maximumFractionDigits: 2,
  });
}

function formatStatus(value) {
  return value ? value.replace(/_/g, " ") : "-";
}

function getInitiatorPoRowClass(row, queueType) {
  if (
    queueType !== "PO_MISSING_VENDOR_OR_INVOICE" && 
    queueType !== "NON_PO_MULTIPLE_CONDITION"
  ) 
    {
    return "";
  }

  if (row.status === "READY_TO_SUBMIT") {
    return "initiator-po-row--ready";
  }

  if (row.status === "PATH_NOT_FOUND") {
    return "initiator-po-row--path-not-found";
  }

  return "";
}

function getConditionRows(conditionValues) {
  const values = conditionValues?.values || {};
  const calculatedValues = conditionValues?.calculatedValues || values;
  const sourceDebug = conditionValues?.sourceDebug || {};

  const conditionKeys = Array.from(
    new Set([...Object.keys(calculatedValues), ...Object.keys(values)])
  );

  return conditionKeys.map((key) => {
    const hasCalculatedValue = Object.prototype.hasOwnProperty.call(
      calculatedValues,
      key
    );

    return {
      key,
      calculatedValue: hasCalculatedValue
        ? calculatedValues[key] || "-"
        : values[key] || "-",
      value: values[key] || "-",
      name: sourceDebug[key]?.name || key,
    };
  });
}

function getApproverRows(matchedApprovalPath) {
  return [
    "approver1",
    "approver2",
    "approver3",
    "approver4",
    "approver5",
    "approver6",
  ].map((field, index) => ({
    field,
    label: `Approver ${index + 1}`,
    value: matchedApprovalPath?.[field] || "-",
  }));
}

function getDuplicateRows(duplicateDetails) {
  if (!duplicateDetails || !Object.keys(duplicateDetails).length) {
    return [];
  }

  return [
    ["Invoice ID", duplicateDetails.invoiceId],
    ["Invoice No.", duplicateDetails.invoiceNo],
    ["Invoice Date", duplicateDetails.invoiceDate],
    ["Invoice Amount", duplicateDetails.invoiceAmount],
    [
      "PDF File",
      duplicateDetails.uniqueName || duplicateDetails.pdfPath,
    ],
  ].filter(
    ([, value]) =>
      value !== undefined &&
      value !== null &&
      value !== ""
  );
}

function buildDraftFromRow(row) {
  if (!row) {
    return {
      selectedInvoiceType: "",
      materialOrService: "",
      vendorGst: "",
      invoiceNo: "",
      invoiceDate: "",
      invoiceAmount: "",
      vendorCode: "",
      selectedPoNumber: "",
    };
  }

  return {
    selectedInvoiceType:
      row.selectedInvoiceType || row.detectedInvoiceType || "",
    materialOrService:
      row.materialOrService || row.detectedMaterialOrService || "",
    vendorGst: row.vendorGst || "",
    invoiceNo: row.invoiceNo || "",
    invoiceDate: row.invoiceDate || "",
    invoiceAmount: row.invoiceAmount || "",
    vendorCode: row.vendorCode || "",
    selectedPoNumber: row.selectedPoNumber || "",
  };
}

export default function InitiatorView() {
  const [activeTab, setActiveTab] = useState(QUEUE_TABS[0].key);
  const [rows, setRows] = useState([]);
  const [selectedRowId, setSelectedRowId] = useState(null);
  const [draft, setDraft] = useState(buildDraftFromRow(null));
  const [editedConditionValues, setEditedConditionValues] = useState({});
  const [previewRow, setPreviewRow] = useState(null);
  const [previewIsCurrent, setPreviewIsCurrent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [recalculating, setRecalculating] = useState(false);
  const [savingPath, setSavingPath] = useState(false);
  const [submittingWorkflow, setSubmittingWorkflow] = useState(false);
  const [error, setError] = useState("");
  const [actionMessage, setActionMessage] = useState("");
  const [activeModal, setActiveModal] = useState("");

  const activeConfig =
    QUEUE_TABS.find((tab) => tab.key === activeTab) || QUEUE_TABS[0];

  const visibleRows = rows.filter(
    (row) => row.queueType === activeConfig.queueType
  );

  const selectedRow =
    rows.find((row) => row.id === selectedRowId) || visibleRows[0] || null;

  const selectedConditionRows = getConditionRows(
    previewRow?.conditionValues || selectedRow?.conditionValues
  );

  const pathResultRow = previewIsCurrent ? previewRow || selectedRow : selectedRow;

  const selectedApproverRows = getApproverRows(
    pathResultRow?.matchedApprovalPath
  );

  const selectedDuplicateRows = getDuplicateRows(
    selectedRow?.duplicateDetails
  );

  const approvalPath = selectedApproverRows
    .filter((approver) => approver.value !== "-")
    .map((approver) => approver.value)
    .join(" → ");

  const hasMatchedApprovalPath = Boolean(
    pathResultRow?.matchedApprovalPath?.id
  );

  const hasEditedConditionValues = selectedConditionRows.some(
    (condition) =>
      Object.prototype.hasOwnProperty.call(
        editedConditionValues,
        condition.key
      ) &&
      editedConditionValues[condition.key] !== condition.value
  );

  const summary = useMemo(
    () =>
      QUEUE_TABS.map((tab) => ({
        ...tab,
        count: rows.filter((row) => row.queueType === tab.queueType).length,
      })),
    [rows]
  );

  async function loadInitiatorRows() {
    setLoading(true);
    setError("");

    try {
      const data = await fetchInvoiceInitiatorQueue();

      setRows(data);

      setSelectedRowId((current) => {
        if (current && data.some((row) => row.id === current)) {
          return current;
        }

        const firstVisible = data.find(
          (row) => row.queueType === activeConfig.queueType
        );

        return firstVisible?.id || null;
      });
    } catch (requestError) {
      setError(requestError.message || "Unable to load Initiator queue.");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadInitiatorRows();

    // Initial queue load only. Action APIs refresh the selected row after recalculate.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setDraft(buildDraftFromRow(selectedRow));
    setActionMessage("");
    setEditedConditionValues({});
    setPreviewRow(null);
    setPreviewIsCurrent(false);

    if (activeModal === "review" && selectedRow) {
      previewInitialPath(selectedRow);
    }
  }, [selectedRow?.id, activeModal]);

  function handleTabChange(tabKey) {
    const nextConfig = QUEUE_TABS.find((tab) => tab.key === tabKey);

    const firstRow = rows.find(
      (row) => row.queueType === nextConfig?.queueType
    );

    setActiveTab(tabKey);
    setSelectedRowId(firstRow?.id || null);
  }

  function openRowModal(rowId, modalName) {
    setSelectedRowId(rowId);
    setActiveModal(modalName);
  }

  function updateDraft(field, value) {
    setDraft((current) => ({
      ...current,
      [field]: value,
    }));
  }

  function updateEditedConditionValue(conditionKey, value) {
    setPreviewIsCurrent(false);
    setEditedConditionValues((current) => ({
      ...current,
      [conditionKey]: value,
    }));
  }

  async function handleRecalculatePath() {
    if (
      !selectedRow ||
      !hasEditedConditionValues
    ) {
      return;
    }

    setRecalculating(true);
    setError("");
    setActionMessage("");

    try {
      const updatedRow = await recalculateInvoiceInitiatorQueue(
        selectedRow.id,
        {
          ...draft,
          selectedConditionValues: editedConditionValues,
          saveFinal: false,
        }
      );

      if (updatedRow) {
        setPreviewRow(updatedRow);
        setPreviewIsCurrent(true);
        setEditedConditionValues(
          updatedRow.conditionValues?.values || {}
        );
        setActionMessage("Approval path preview recalculated.");
      }
    } catch (requestError) {
      setError(
        requestError.message || "Unable to recalculate approval path."
      );
    } finally {
      setRecalculating(false);
    }
  }

  async function previewInitialPath(row) {
    setRecalculating(true);
    setError("");

    try {
      const updatedRow = await recalculateInvoiceInitiatorQueue(row.id, {
        ...buildDraftFromRow(row),
        selectedConditionValues: row.conditionValues?.values || {},
        saveFinal: false,
      });

      if (updatedRow) {
        setPreviewRow(updatedRow);
        setPreviewIsCurrent(true);
        setEditedConditionValues(
          updatedRow.conditionValues?.values || {}
        );
      }
    } catch (requestError) {
      setError(requestError.message || "Unable to calculate approval path.");
    } finally {
      setRecalculating(false);
    }
  }

  async function handleSavePath() {
    if (!selectedRow || !previewRow?.matchedApprovalPath?.id) {
      return;
    }

    setSavingPath(true);
    setError("");
    setActionMessage("");

    try {
      const updatedRow = await recalculateInvoiceInitiatorQueue(
        selectedRow.id,
        {
          ...draft,
          selectedConditionValues: editedConditionValues,
          saveFinal: true,
        }
      );

      if (updatedRow) {
        setRows((currentRows) =>
          currentRows.map((row) =>
            row.id === updatedRow.id ? updatedRow : row
          )
        );
        setPreviewRow(null);
        setPreviewIsCurrent(false);
        setEditedConditionValues({});
        setActionMessage("Approval path saved.");
      }
    } catch (requestError) {
      setError(requestError.message || "Unable to save approval path.");
    } finally {
      setSavingPath(false);
    }
  }

  async function handleSubmitToWorkflow(rowId = selectedRow?.id) {
    if (!rowId) {
      return;
    }

    setSubmittingWorkflow(true);
    setError("");
    setActionMessage("");

    try {
      const response = await submitInvoiceInitiatorToWorkflow(rowId);

      setActionMessage(
        response.message || "Invoice submitted to approval workflow."
      );

      await loadInitiatorRows();
    } catch (requestError) {
      setError(
        requestError.message || "Unable to submit invoice to workflow."
      );
    } finally {
      setSubmittingWorkflow(false);
    }
  }

  return (
    <section className="initiator-view">

      <div className="workspace-panel initiator-hero initiator-hero--compact">
        <div className="initiator-hero__content">
          <div className="initiator-hero__copy">
          {/* <p className="eyebrow">Invoice Approval</p> */}
          <h2>Invoice Approval Initiator Queue</h2>
            <p className="initiator-hero__intro">
              Review invoice approval paths and submit confirmed invoices.
            </p>
          </div>

          <div className="initiator-summary-grid">
            {summary.map((item) => (
              <article className="initiator-summary-card" key={item.key}>
                <p className="eyebrow">{item.label}</p>
                <strong>{item.count}</strong>
              </article>
            ))}
          </div>

        </div>
      </div>
      

      {/* <div className="workspace-panel initiator-section-tabs">
        <div className="initiator-queue-selector">
          <label htmlFor="queueType">Queue Type</label>

          <select
            id="queueType"
            value={activeTab}
            onChange={(e) => handleTabChange(e.target.value)}
          >
            {summary.map((item) => (
              <option key={item.key} value={item.key}>
                {item.label} ({item.count})
              </option>
            ))}
          </select>
        </div>
      </div>  */}
      
      <div className="workspace-panel initiator-queue-panel">
        <div className="processor-table__header">
          <div
            className="initiator-queue-tabs"
            role="group"
            aria-label="Initiator queue filter"
          >
            {summary.map((item) => (
              <button
                className={`initiator-queue-tabs__button${
                  activeTab === item.key ? " is-active" : ""
                }`}
                key={item.key}
                type="button"
                aria-pressed={activeTab === item.key}
                onClick={() => handleTabChange(item.key)}
              >
                <span>{item.label}</span>
                <strong>{item.count}</strong>
              </button>
            ))}
          </div>

          <div className="config-inline">
            <button
              className="ghost-button"
              type="button"
              onClick={loadInitiatorRows}
              disabled={loading}
            >
              {loading ? "Refreshing..." : "Refresh"}
            </button>
          </div>
        </div>

        {error ? (
          <div className="config-banner config-banner--error">{error}</div>
        ) : null}

        <div className="config-table processor-table initiator-table">
          <table>
            <thead>
              <tr>
                {activeConfig.queueType === "DUPLICATE_INVOICE" ? (
                  <>
                    <th>Invoice ID</th>
                    <th>Invoice No.</th>
                    <th>Vendor Name (As Per OCR)</th>
                    <th>Invoice Date</th>
                    <th>Invoice Amount</th>
                    <th>Pdf Copy of Invoice</th>
                    <th>Details of Duplicate Invoice</th>
                    <th>Submit</th>
                  </>
                ) : (
                  <>
                    <th>Invoice ID</th>
                    <th>Invoice No.</th>
                    <th>Vendor Name</th>
                    <th>Vendor Code</th>
                    <th>Invoice Date</th>
                    <th>Invoice Amount</th>
                    <th>Vendor GST</th>
                    <th>Status</th>
                    <th>PDF</th>
                    <th>Approval Path</th>
                    <th>Action</th>
                  </>
                )}
              </tr>
            </thead>

            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan={
                      activeConfig.queueType === "DUPLICATE_INVOICE"
                        ? 8
                        : 11
                    }
                  >
                    Loading Initiator queue...
                  </td>
                </tr>
              ) : visibleRows.length ? (
                visibleRows.map((row) => (
                  <tr
                    key={row.id}
                    className={getInitiatorPoRowClass(
                      row,
                      activeConfig.queueType
                    )}
                  >
                    {activeConfig.queueType === "DUPLICATE_INVOICE" ? (
                      <>
                        {/* Current Invoice */}
                        <td>{row.invoiceId || "-"}</td>
                        <td>{row.invoiceNo || "-"}</td>
                        <td>{row.vendorNameOcr || "-"}</td>
                        <td>{row.invoiceDate || "-"}</td>
                        <td>{formatAmount(row.invoiceAmount)}</td>

                        {/* Current Invoice PDF */}
                        <td>
                          {row.uniqueName ? (
                            <button
                              type="button"
                              className="pdf-view-button"
                              onClick={() => openInvoicePdf(row.invoiceId)}
                            >
                              View PDF
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>


                        {/* Details of Duplicate Invoice */}
                        <td>
                          {row.duplicateDetails &&
                            Object.keys(row.duplicateDetails).length ? (
                            <div>
                              <div>
                                <strong>Invoice ID:</strong>{" "}
                                {row.duplicateDetails.invoiceId || "-"}
                              </div>

                              <div>
                                <strong>Invoice No.:</strong>{" "}
                                {row.duplicateDetails.invoiceNo || "-"}
                              </div>

                              <div>
                                <strong>Invoice Date:</strong>{" "}
                                {row.duplicateDetails.invoiceDate || "-"}
                              </div>

                              <div>
                                <strong>Invoice Amount:</strong>{" "}
                                {formatAmount(
                                  row.duplicateDetails.invoiceAmount
                                )}
                              </div>

                              <div>
                                <strong>PDF:</strong>{" "}
                                {row.duplicateDetails.uniqueName ||
                                  row.duplicateDetails.pdfPath
                                  ? "View"
                                  : "-"}
                              </div>
                            </div>
                          ) : (
                            "-"
                          )}
                        </td>

                        {/* Submit */}
                        <td>
                          <button
                            className="primary-button"
                            type="button"
                          >
                            Submit
                          </button>
                        </td>
                      </>
                    ) : (
                      <>
                        <td>{row.invoiceId}</td>
                        <td>{row.invoiceNo}</td>
                        <td>{row.vendorNameOcr}</td>
                        <td>{row.vendorCode}</td>
                        <td>{row.invoiceDate}</td>
                        <td>{formatAmount(row.invoiceAmount)}</td>
                        <td>{row.vendorGst}</td>

                        <td>{formatStatus(row.status)}</td>

                        <td>
                          {row.uniqueName ? (
                            <button
                              type="button"
                              className="pdf-view-button"
                              onClick={() => openInvoicePdf(row.invoiceId)}
                            >
                              View PDF
                            </button>
                          ) : (
                            "-"
                          )}
                        </td>

                        <td>
                          <div className="initiator-action-buttons">
                            <button
                              className="ghost-button"
                              type="button"
                              onClick={() =>
                                openRowModal(row.id, "review")
                              }
                            >
                              View Path
                            </button>
                          </div>
                        </td>

                        <td>
                          <div className="initiator-action-buttons">
                            <button
                              className="primary-button initiator-workflow-button"
                              type="button"
                              title="Submit to Workflow"
                              aria-label="Submit to Workflow"
                              onClick={() => handleSubmitToWorkflow(row.id)}
                              disabled={
                                submittingWorkflow ||
                                !row.matchedApprovalPath?.id ||
                                row.status !== "READY_TO_SUBMIT"
                              }
                            >
                              {submittingWorkflow
                                ? "Submitting..."
                                : "Submit to Workflow"}
                            </button>
                          </div>
                        </td>
                      </>
                    )}
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={
                      activeConfig.queueType === "DUPLICATE_INVOICE"
                        ? 8
                        : 11
                    }
                  >
                    No invoices found in this queue.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {activeModal && selectedRow ? (
        <div
          className="initiator-modal-backdrop"
          role="presentation"
          onClick={() => setActiveModal("")}
        >
          <div
            className="initiator-modal"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="initiator-modal__header">
              <div>
                <p className="eyebrow">Invoice Workspace</p>

                <h2>
                  {selectedRow.invoiceNo || "Invoice Review"}
                </h2>
              </div>

              <button
                className="ghost-button"
                type="button"
                onClick={() => setActiveModal("")}
              >
                Close
              </button>
            </div>

            {activeModal === "review" ? (
              <div className="workspace-panel initiator-detail initiator-modal-panel">
                <div className="processor-table__header">
                  <div>
                    <p className="eyebrow">Invoice Workspace</p>

                    <h2>
                      {selectedRow.invoiceNo ||
                        "No invoice selected"}
                    </h2>
                  </div>

                </div>

                {actionMessage ? (
                  <div className="config-banner">
                    {actionMessage}
                  </div>
                ) : null}

                {/* =========================
                    SECTION 2: CALCULATED CONDITIONS
                   ========================= */}

                <section className="initiator-result-section">
                  <div className="initiator-result-section__header">
                    <div>
                      <p className="eyebrow">
                        Calculated Conditions
                      </p>

                      <strong>
                        {selectedConditionRows.length} condition
                        values
                      </strong>
                    </div>
                  </div>

                  {selectedConditionRows.length ? (
                    <div className="config-table initiator-result-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Condition</th>
                            <th>System Calculated</th>
                            <th>Select Value</th>
                          </tr>
                        </thead>

                        <tbody>
                          {selectedConditionRows.map(
                            (condition) => (
                              <tr key={condition.key}>
                                <td>{condition.name}</td>
                                <td>{condition.calculatedValue}</td>
                                <td>
                                  <select
                                    className="initiator-condition-input"
                                    value={
                                      editedConditionValues[
                                      condition.key
                                      ] ??
                                      condition.value ??
                                      ""
                                    }
                                    onChange={(event) =>
                                      updateEditedConditionValue(
                                        condition.key,
                                        event.target.value
                                      )
                                    }
                                  >
                                    {!selectedRow.conditionOptions?.[
                                      condition.key
                                    ]?.includes(condition.value) ? (
                                      <option value={condition.value}>
                                        {condition.value}
                                      </option>
                                    ) : null}
                                    {selectedRow.conditionOptions?.[
                                      condition.key
                                    ]?.map((value) => (
                                      <option key={value} value={value}>
                                        {value}
                                      </option>
                                    ))}
                                  </select>
                                </td>
                              </tr>
                            )
                          )}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <p className="config-note">
                      No calculated condition values stored yet.
                    </p>
                  )}
                </section>

                <div className="config-inline">
                  <button
                    className="ghost-button"
                    type="button"
                    onClick={handleRecalculatePath}
                    disabled={
                      !selectedRow ||
                      !hasEditedConditionValues ||
                      recalculating
                    }
                  >
                    {recalculating
                      ? "Recalculating..."
                      : "Recalculate Path"}
                  </button>
                </div>

                {/* =========================
                    SECTION 3: CALCULATED APPROVAL PATH
                   ========================= */}

                <section className="initiator-result-section">
                  <div className="initiator-result-section__header">
                    <div>
                      <p className="eyebrow">
                        Calculated Approval Path
                      </p>

                      <strong>
                        {hasMatchedApprovalPath
                          ? `Matched #${pathResultRow.matchedApprovalPath.id}`
                          : "Not matched"}
                      </strong>
                    </div>
                  </div>

                  {hasMatchedApprovalPath && approvalPath ? (
                    <div className="initiator-approval-path">
                      {approvalPath}
                    </div>
                  ) : (
                    <p className="config-note">
                      No approval path is matched for this invoice
                      yet.
                    </p>
                  )}
                </section>

                {previewIsCurrent && previewRow?.matchedApprovalPath?.id ? (
                  <div className="config-inline">
                    <button
                      className="primary-button"
                      type="button"
                      onClick={handleSavePath}
                      disabled={savingPath}
                    >
                      {savingPath ? "Saving..." : "Save"}
                    </button>
                  </div>
                ) : null}

              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </section>
  );
}
